/**
 * The federal-tax order status registry (spec 2026-09-26, section 8).
 *
 * `service_data.status` moves only along `TRANSITIONS`. Every API write goes
 * through `transitionOrderStatus` (apps/api/src/federal-tax/transition.ts),
 * which checks `canTransition` and guards the write atomically.
 */

export const FEDERAL_TAX_STATUSES = [
  "not_started",
  "in_progress",
  "submitted",
  "preparing",
  "awaiting_signature",
  "signature_review",
  "needs_changes",
  "sending",
  "filed",
  "completed",
  "on_hold",
  "cancelled",
  "refunded",
] as const;

export type FederalTaxStatus = (typeof FEDERAL_TAX_STATUSES)[number];

/** The status a new order is created with. */
export const INITIAL_FEDERAL_TAX_STATUS: FederalTaxStatus = "not_started";

/** The six locked badge tones both apps render. */
export type StatusTone =
  "success" | "progress" | "pending" | "attention" | "critical" | "special";

export interface StatusMeta {
  customerLabel: string;
  adminLabel: string;
  tone: StatusTone;
  terminal: boolean;
}

function current(
  customerLabel: string,
  adminLabel: string,
  tone: StatusTone,
  terminal = false,
): StatusMeta {
  return { customerLabel, adminLabel, tone, terminal };
}

/**
 * What to do on a filing sent back (`needs_changes`), by cause (copy rollout
 * S04): a rejected signature page, or answers our team or the forms could not
 * use. `STATUS_META.needs_changes.customerLabel` is the answers one; a surface
 * that knows a signature page was rejected shows the signature one.
 */
export const NEEDS_CHANGES_CUSTOMER_LABELS = {
  answers: "Update your answers",
  signature: "Fix and sign again",
} as const;

export const STATUS_META: Record<FederalTaxStatus, StatusMeta> = {
  not_started: current("Not started", "Not started", "pending"),
  in_progress: current("In progress", "In progress", "progress"),
  submitted: current("Submitted", "Submitted", "progress"),
  preparing: current("Preparing your forms", "Preparing forms", "progress"),
  awaiting_signature: current(
    "Ready to sign",
    "Awaiting signature",
    "attention",
  ),
  signature_review: current(
    "Checking your signature",
    "Signature review",
    "attention",
  ),
  needs_changes: current(
    NEEDS_CHANGES_CUSTOMER_LABELS.answers,
    "Needs changes",
    "critical",
  ),
  sending: current("Sending to the IRS", "Sending", "progress"),
  filed: current("Filed with the IRS", "Filed with IRS", "success"),
  completed: current("Completed", "Completed", "success", true),
  on_hold: current("On hold", "On hold", "special"),
  cancelled: current("Canceled", "Cancelled", "pending", true),
  refunded: current("Refunded", "Refunded", "special", true),
};

export const TERMINAL_STATUSES: readonly FederalTaxStatus[] =
  FEDERAL_TAX_STATUSES.filter((status) => STATUS_META[status].terminal);

/**
 * Nothing more is due: the return is filed, or the order is closed. Every read
 * that asks "is this filing overdue?" (the deadlines list, monitoring, the
 * admin aggregates) leaves these out.
 */
export const NO_DEADLINE_STATUSES: readonly FederalTaxStatus[] = [
  "filed",
  ...TERMINAL_STATUSES,
];

export function isPastDeadline(value: unknown): boolean {
  return (
    isFederalTaxStatus(value) &&
    (NO_DEADLINE_STATUSES as readonly string[]).includes(value)
  );
}

/** Admin exits available from every non-terminal status (spec 8, last rows). */
const EXITS = ["on_hold", "cancelled", "refunded"] as const;

/**
 * The single transition table. `→ preparing` from the three signature states is
 * the admin "Rebuild packet" action (spec 9 and 13), which starts a new packet
 * build;
 * `→ filed` and `→ needs_changes` from `submitted` / `preparing` /
 * `awaiting_signature` are the console's manual close path ("Mark filed" and
 * "Send back to customer", open-work spec B-1): a filing the team prepares by
 * hand never reaches the packet review that would otherwise move it on.
 */
export const TRANSITIONS: Record<
  FederalTaxStatus,
  readonly FederalTaxStatus[]
> = {
  not_started: ["in_progress", ...EXITS],
  in_progress: ["submitted", ...EXITS],
  submitted: ["preparing", "filed", "needs_changes", ...EXITS],
  preparing: ["awaiting_signature", "needs_changes", "filed", ...EXITS],
  awaiting_signature: [
    "signature_review",
    "preparing",
    "filed",
    "needs_changes",
    ...EXITS,
  ],
  signature_review: ["needs_changes", "sending", "preparing", ...EXITS],
  needs_changes: ["submitted", "awaiting_signature", "preparing", ...EXITS],
  sending: ["filed", ...EXITS],
  filed: ["completed", ...EXITS],
  on_hold: [
    "not_started",
    "in_progress",
    "submitted",
    "preparing",
    "awaiting_signature",
    "signature_review",
    "needs_changes",
    "sending",
    "filed",
    "cancelled",
    "refunded",
  ],
  completed: [],
  cancelled: [],
  refunded: [],
};

const CURRENT = new Set<string>(FEDERAL_TAX_STATUSES);

export function isFederalTaxStatus(value: unknown): value is FederalTaxStatus {
  return typeof value === "string" && CURRENT.has(value);
}

/**
 * The status a stored value stands for: itself, or `not_started` when nothing
 * is stored yet. `null` for anything unknown.
 */
export function normalizeStatus(value: unknown): FederalTaxStatus | null {
  if (value === null || value === undefined) return INITIAL_FEDERAL_TAX_STATUS;
  if (isFederalTaxStatus(value)) return value;
  return null;
}

export function isTerminalStatus(value: unknown): boolean {
  return isFederalTaxStatus(value) && STATUS_META[value].terminal;
}

/**
 * Whether `from → to` is a transition. A same-status write is not one. `from`
 * may be a stored value (missing reads as `not_started`); `to`
 * must be a current status.
 */
export function canTransition(from: unknown, to: unknown): boolean {
  if (!isFederalTaxStatus(to)) return false;
  const normalized = normalizeStatus(from);
  if (normalized === null || normalized === to) return false;
  return TRANSITIONS[normalized].includes(to);
}

/** Targets an admin may set by hand (spec 8 "Trigger" column). */
const ADMIN_TARGETS = new Set<FederalTaxStatus>([
  "on_hold",
  "cancelled",
  "refunded",
  "completed",
  "needs_changes",
]);

/**
 * Whether an admin may write `to` directly (the admin PATCH). Customer- and
 * system-driven edges (submit, packet build, upload, review, fax) have their
 * own routes and are refused here. Leaving `on_hold` may go back to any status
 * the table allows.
 */
export function canAdminTransition(from: unknown, to: unknown): boolean {
  if (!canTransition(from, to) || !isFederalTaxStatus(to)) return false;
  if (normalizeStatus(from) === "on_hold") return true;
  return ADMIN_TARGETS.has(to);
}

// --- hold, resume, cancel ----------------------------------------------------
//
// The console's order controls (spec 2026-09-27 phase B). Holding records the
// status the order was in (the API stores it under `service_data.hold.from`),
// and resuming returns to exactly that status — `TRANSITIONS.on_hold` alone
// would allow any open status, which is the table's reach, not the rule.

/** Open statuses a hold may return to: `on_hold`'s exits minus the closing ones. */
export const RESUMABLE_STATUSES: readonly FederalTaxStatus[] =
  TRANSITIONS.on_hold.filter((status) => !STATUS_META[status].terminal);

/** Whether an order in `from` may be put on hold. */
export function canHold(from: unknown): boolean {
  return canTransition(from, "on_hold");
}

/**
 * The status an order held from `heldFrom` resumes to (a missing value as
 * `not_started`). `null` when that is not an open status a hold can return to.
 */
export function resumeTarget(heldFrom: unknown): FederalTaxStatus | null {
  const target = normalizeStatus(heldFrom);
  return target !== null && RESUMABLE_STATUSES.includes(target) ? target : null;
}

/** Whether an order in `from` (a hold included) may be cancelled. */
export function canCancel(from: unknown): boolean {
  return canTransition(from, "cancelled");
}

// --- packet rebuild ---------------------------------------------------------

/**
 * The statuses an admin "Rebuild
 * packet" starts from: before a build, and while the customer's signature is
 * awaited, reviewed or sent back. Once a return is sending, filed or closed a
 * rebuild would put a second copy next to one that is real at the IRS, so the
 * API refuses it and the console does not offer it.
 */
export const REBUILDABLE_STATUSES = [
  "submitted",
  "preparing",
  "awaiting_signature",
  "signature_review",
  "needs_changes",
] as const satisfies readonly FederalTaxStatus[];

/** Whether the console's "Rebuild packet" applies to an order in `from`. */
export function canRebuildPackets(from: unknown): boolean {
  const normalized = normalizeStatus(from);
  return (
    normalized !== null &&
    (REBUILDABLE_STATUSES as readonly FederalTaxStatus[]).includes(normalized)
  );
}

// --- manual close path ------------------------------------------------------
//
// Open-work spec B-1 (owner decision B1, 2026-09-28). A filing the team
// prepares by hand (MMLLC, a tax year the packet build does not cover, a fill
// error, a DocuSeal-era order) never passes packet review, so the console
// closes it by hand: "Mark filed" records how and when the team filed it, and
// "Send back to customer" reopens the wizard with a note.

/** The statuses the manual close starts from. */
export const MANUAL_CLOSE_FROM = [
  "submitted",
  "preparing",
  "awaiting_signature",
] as const satisfies readonly FederalTaxStatus[];

/** How the team filed a return by hand. */
export const MANUAL_FILING_METHODS = ["mailed", "faxed", "efiled"] as const;
export type ManualFilingMethod = (typeof MANUAL_FILING_METHODS)[number];

function inManualCloseStatus(from: unknown): boolean {
  if (from === null || from === undefined) return false;
  const normalized = normalizeStatus(from);
  return (
    normalized !== null &&
    (MANUAL_CLOSE_FROM as readonly FederalTaxStatus[]).includes(normalized)
  );
}

/** Whether the console's "Mark filed" applies to an order in `from`. */
export function canMarkFiled(from: unknown): boolean {
  return inManualCloseStatus(from) && canTransition(from, "filed");
}

/** Whether the console's "Send back to customer" applies to an order in `from`. */
export function canSendBack(from: unknown): boolean {
  return inManualCloseStatus(from) && canTransition(from, "needs_changes");
}

/** The pipeline stages the console's stat cards count (spec 10.2, fix C3). */
export type FederalTaxStage =
  "not_started" | "in_progress" | "signature" | "filed";

const STAGE: Record<FederalTaxStatus, FederalTaxStage | null> = {
  not_started: "not_started",
  in_progress: "in_progress",
  submitted: "in_progress",
  preparing: "in_progress",
  needs_changes: "in_progress",
  sending: "in_progress",
  awaiting_signature: "signature",
  signature_review: "signature",
  filed: "filed",
  completed: "filed",
  // Exceptions to the pipeline, not a stage of ordinary progress.
  on_hold: null,
  cancelled: null,
  refunded: null,
};

/** The stage a stored status counts under; null for none. */
export function statusStage(value: unknown): FederalTaxStage | null {
  return isFederalTaxStatus(value) ? STAGE[value] : null;
}
